// AD-13 | New cart screen: replaces the add-to-cart notification and the trip to /cart.
//   control = today's cart notification + /cart page
//   v1      = new cart screen: full screen on mobile, drawer from the right on desktop. Opens after
//             any add to cart and when the header cart icon is tapped. /cart stays live for anyone
//             who lands on it directly.
// Both groups report cart_opened (add_to_cart / cart_icon), checkout_clicked and
// continue_shopping_clicked to GA4.
// Not included (decisions): Shipped Shield row, protection plan, express pay buttons, variant names.
//
// Wrapped in its own function scope: other test scripts on the page (e.g. ad-11, loaded site-wide)
// also declare top-level `const iframe` / `realLog`, and top-level consts are shared across all
// scripts, so a second declaration would stop this whole file with a SyntaxError.
(function () {

// 1. Create an invisible iframe to bypass the theme's silencer
const iframe = document.createElement('iframe');
iframe.style.display = 'none';
document.body.appendChild(iframe);

// 2. Steal its untouched console.log and warn functions
const realLog = iframe.contentWindow.console.log;
const realWarn = iframe.contentWindow.console.warn;

window.gbEvents = window.gbEvents || [];

// =========================================================================
// ---> EVENT TRACKING (both groups; switched on by the randomizer below)
// =========================================================================
const AD13 = {
	testId: 'AD13',
	variant: null,
	reporting: false, // true once a live (non-preview) visitor is assigned
	shopUrl: '/collections/airdog-air-purifiers', // empty cart button
};

function ad13Track(eventName, params) {
	if (!AD13.reporting) return;
	window.dataLayer = window.dataLayer || [];
	function gtag() {
		dataLayer.push(arguments);
	}
	gtag('event', eventName, {
		experiment_id: AD13.testId,
		experiment_variant: AD13.variant,
		send_to: 'G-Y4VGNLBJPF',
		// These often fire right before the page changes (checkout, /cart), so send with sendBeacon.
		transport_type: 'beacon',
		...params,
	});
}

// Control group: same events from today's cart notification, cart icon and /cart page.
// (v1 tracks from the cart screen in testCode.)
function ad13TrackControl() {
	customElements.whenDefined('cart-notification').then(() => {
		const notification = document.querySelector('cart-notification');
		if (!notification || notification.ad13Wrapped) return;
		notification.ad13Wrapped = true;
		const originalRender = notification.renderContents.bind(notification);
		notification.renderContents = (parsedState) => {
			ad13Track('cart_opened', { cart_source: 'add_to_cart' });
			return originalRender(parsedState);
		};
	});

	document.addEventListener(
		'click',
		(event) => {
			if (event.target.closest('#cart-icon-bubble, [data-id="mobile-cart-icon"]')) {
				ad13Track('cart_opened', { cart_source: 'cart_icon' });
			}
			if (event.target.closest('.title-wrapper-with-link .underlined-link, .cart__warnings a.button')) {
				ad13Track('continue_shopping_clicked', { cart_location: 'cart_page' });
			}
		},
		true,
	);

	document.addEventListener(
		'submit',
		(event) => {
			if (!event.submitter || event.submitter.name !== 'checkout') return;
			const location = event.target.closest('#cart-notification') ? 'cart_notification' : 'cart_page';
			ad13Track('checkout_clicked', { cart_location: location });
		},
		true,
	);
}

function testCode() {
	(function initAirdogCartScreen() {
		if (document.getElementById('ad13-cart')) return;

		realLog(
			'%cAD-13 | New cart screen',
			"color: white; background-color:#202621; font-weight: bold; font-family:'SpaceGrotesk', sans-serif;",
		);

		// ---------------------------------------------------------------------
		// Helpers
		// ---------------------------------------------------------------------
		const money = (cents, currency) =>
			new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((cents || 0) / 100);

		const escapeHtml = (value) =>
			String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

		const sizedImage = (url, width) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + width : '');

		const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

		const ICONS = {
			close:
				'<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
			check:
				'<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
			trash:
				'<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>',
			box: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>',
			home: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>',
			headset:
				'<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z"/><path d="M21 16v2a4 4 0 0 1-4 4h-5"/></svg>',
		};

		// ---------------------------------------------------------------------
		// Styles (scoped to #ad13-cart)
		// ---------------------------------------------------------------------
		const style = document.createElement('style');
		style.id = 'ad13-cart-styles';
		style.innerHTML = `
#ad13-cart {
	--ad13-ink: #111820;
	--ad13-muted: #555;
	--ad13-line: #dbe4ef;
	--ad13-green: #9ece7b;
	--ad13-green-hover: #b6dd9b;
	--ad13-blue: #005bac;
	position: fixed;
	inset: 0;
	/* Above third-party widgets (chat, accessibility, quiz), which use the max z-index too;
	   JS also moves this to the end of <body> on open so it wins the tie. */
	z-index: 2147483647;
	font-family: 'Atkinson Hyperlegible', 'Atkinson-Hyperlegible', sans-serif;
	color: var(--ad13-ink);
	letter-spacing: normal;
}
#ad13-cart[hidden] { display: none; }
#ad13-cart *, #ad13-cart *::before, #ad13-cart *::after { box-sizing: border-box; }
/*
  Resets use :where(#ad13-cart) so they carry element-level specificity only: they still override
  the theme's element styles (they load later), but every .ad13-* class rule below wins over them.
*/
:where(#ad13-cart) :is(h2, p, ul, li, form) { margin: 0; padding: 0; }
:where(#ad13-cart) ul { list-style: none; }
:where(#ad13-cart) button {
	appearance: none;
	margin: 0;
	border: 0;
	background: transparent;
	color: inherit;
	font: inherit;
	cursor: pointer;
	box-shadow: none;
	min-height: 0;
}
:where(#ad13-cart) button::before, :where(#ad13-cart) button::after { display: none; }
:where(#ad13-cart) a { color: inherit; }
#ad13-cart :focus-visible { outline: 2px solid var(--ad13-blue); outline-offset: 2px; }
#ad13-cart .visually-hidden {
	position: absolute !important;
	width: 1px; height: 1px; margin: -1px; padding: 0;
	overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

.ad13-overlay { position: absolute; inset: 0; background: rgb(17 24 32 / 0.55); opacity: 0; transition: opacity 0.25s ease; }
.ad13-panel {
	position: absolute;
	top: 0; right: 0; bottom: 0;
	width: min(420px, 100%);
	display: flex;
	flex-direction: column;
	background: #fff;
	box-shadow: -10px 0 30px rgb(0 0 0 / 0.15);
	transform: translateX(100%);
	transition: transform 0.3s ease;
	outline: none;
}
#ad13-cart.is-open .ad13-overlay { opacity: 1; }
#ad13-cart.is-open .ad13-panel { transform: none; }

.ad13-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin: 0 22px;
	padding: 24px 0 14px;
}
.ad13-title {
	font-family: 'Space Grotesk', 'SpaceGrotesk', sans-serif;
	font-size: 30px;
	font-weight: 400;
	line-height: 1.15;
	letter-spacing: -0.01em;
	color: var(--ad13-ink);
}
.ad13-close { display: grid; place-items: center; width: 40px; height: 40px; margin-right: -8px; border-radius: 6px; }
.ad13-added {
	display: flex; align-items: center; gap: 6px;
	margin: 0 22px; padding-bottom: 16px;
	font-size: 13px; color: var(--ad13-ink);
}
.ad13-added[hidden] { display: none; }
.ad13-added svg { color: #2e7d32; flex-shrink: 0; }
.ad13-rule { margin: 0 22px; border-top: 1px solid var(--ad13-line); }

.ad13-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 0 22px; }
.ad13-loading { padding: 24px 0; font-size: 14px; color: var(--ad13-muted); }
.ad13-error { margin-top: 14px; padding: 10px 12px; border-radius: 6px; background: #fdecea; color: #8a1c13; font-size: 13px; }
.ad13-error[hidden] { display: none; }

.ad13-item {
	display: grid;
	grid-template-columns: 62px minmax(0, 1fr) auto;
	gap: 16px;
	align-items: start;
	padding: 20px 0;
	border-bottom: 1px solid var(--ad13-line);
}
.ad13-item__img {
	display: block; width: 62px; height: 62px;
	border: 1px solid var(--ad13-line); border-radius: 6px;
	overflow: hidden; background: #fff;
}
.ad13-item__img img { display: block; width: 100%; height: 100%; object-fit: contain; }
.ad13-item__title {
	display: block;
	font-family: 'Space Grotesk', 'SpaceGrotesk', sans-serif;
	font-size: 16px; line-height: 1.25; color: var(--ad13-ink);
	text-decoration: none;
}
.ad13-item__title:hover { text-decoration: underline; }
.ad13-item__price { margin-top: 6px; font-size: 14px; color: var(--ad13-ink); }
.ad13-item__price s { margin-right: 6px; color: var(--ad13-muted); }
.ad13-item__actions { display: flex; flex-direction: column; align-items: center; gap: 10px; }
.ad13-qty {
	display: inline-flex; align-items: center;
	height: 34px; border: 1px solid var(--ad13-line); border-radius: 6px;
}
.ad13-qty button { width: 30px; height: 32px; font-size: 16px; line-height: 1; }
.ad13-qty button[disabled] { opacity: 0.35; cursor: not-allowed; }
.ad13-qty__value { min-width: 22px; text-align: center; font-size: 14px; }
.ad13-remove { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 6px; color: #8a96a3; }
.ad13-remove:hover { color: var(--ad13-ink); }
#ad13-cart.is-busy .ad13-item__actions button { pointer-events: none; opacity: 0.5; }

.ad13-empty { padding: 32px 0; text-align: center; }
.ad13-empty[hidden] { display: none; }
.ad13-empty p { font-size: 16px; margin-bottom: 18px; }

.ad13-btn {
	display: flex; align-items: center; justify-content: center;
	width: 100%; min-height: 46px; padding: 10px 18px;
	border-radius: 6px; background: var(--ad13-green); color: var(--ad13-ink) !important;
	font-family: 'Space Grotesk', 'SpaceGrotesk', sans-serif; font-size: 17px;
	text-decoration: none; transition: background-color 0.2s ease;
}
.ad13-btn:hover { background: var(--ad13-green-hover); }

.ad13-foot { flex-shrink: 0; padding: 20px 22px calc(20px + env(safe-area-inset-bottom, 0px)); border-top: 1px solid var(--ad13-line); background: #fff; }
.ad13-foot[hidden] { display: none; }
.ad13-subtotal { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.ad13-subtotal__label { font-size: 18px; }
.ad13-subtotal__value { font-family: 'Space Grotesk', 'SpaceGrotesk', sans-serif; font-size: 26px; font-weight: 600; letter-spacing: -0.01em; }
/* Three guarantees spread across the row; each label may wrap to two lines on narrow screens */
.ad13-trust {
	display: flex; justify-content: space-between; gap: 10px;
	margin-top: 12px;
	font-size: 11px; line-height: 1.3; color: var(--ad13-muted);
}
.ad13-trust li { display: flex; align-items: flex-start; gap: 5px; min-width: 0; }
.ad13-trust svg { flex-shrink: 0; margin-top: 1px; }
.ad13-actions { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 20px; margin-top: 18px; }
.ad13-continue { font-size: 14px; text-decoration: underline; text-underline-offset: 3px; padding: 8px 0; }
.ad13-checkout { cursor: pointer; }

/* Mobile: full screen under the site header */
@media screen and (max-width: 767.98px) {
	/* The whole screen starts at the bottom of the site header (set by JS when it opens) */
	#ad13-cart { top: var(--ad13-top, 0px); }
	.ad13-overlay { display: none; }
	.ad13-panel {
		width: 100%;
		box-shadow: none;
		transform: translateY(16px);
		opacity: 0;
		transition: transform 0.25s ease, opacity 0.25s ease;
	}
	#ad13-cart.is-open .ad13-panel { transform: none; opacity: 1; }
	.ad13-head, .ad13-added, .ad13-rule { margin-left: 16px; margin-right: 16px; }
	.ad13-body { padding: 0 16px; }
	.ad13-foot { padding-left: 16px; padding-right: 16px; }
	.ad13-title { font-size: 30px; }
}

@media (prefers-reduced-motion: reduce) {
	.ad13-overlay, .ad13-panel { transition: none !important; }
}
`;
		document.head.appendChild(style);

		// ---------------------------------------------------------------------
		// Markup
		// ---------------------------------------------------------------------
		document.body.insertAdjacentHTML(
			'beforeend',
			`<div id="ad13-cart" hidden>
	<div class="ad13-overlay" data-ad13-close></div>
	<div class="ad13-panel" role="dialog" aria-modal="true" aria-labelledby="ad13-title" tabindex="-1">
		<div class="ad13-head">
			<h2 class="ad13-title" id="ad13-title">Your cart</h2>
			<button type="button" class="ad13-close" data-ad13-close aria-label="Close cart">${ICONS.close}</button>
		</div>
		<p class="ad13-added" hidden>${ICONS.check}<span>Item added to your cart</span></p>
		<div class="ad13-rule"></div>
		<div class="ad13-body">
			<p class="ad13-loading">Loading your cart…</p>
			<ul class="ad13-items" aria-label="Items in your cart"></ul>
			<div class="ad13-empty" hidden>
				<p>Your cart is empty.</p>
				<a class="ad13-btn" href="${AD13.shopUrl}">Shop air purifiers</a>
			</div>
			<p class="ad13-error" role="alert" hidden></p>
		</div>
		<div class="ad13-foot" hidden>
			<div class="ad13-subtotal">
				<span class="ad13-subtotal__label">Subtotal:</span>
				<span class="ad13-subtotal__value" data-ad13-subtotal></span>
			</div>
			<ul class="ad13-trust" aria-label="Shipping and service guarantees">
				<li>${ICONS.box}Fast, Free Shipping</li>
				<li>${ICONS.home}30-Day Home Trial</li>
				<li>${ICONS.headset}24/7 Customer Service</li>
			</ul>
			<div class="ad13-actions">
				<button type="button" class="ad13-continue" data-ad13-continue>Continue shopping</button>
				<form action="${root}cart" method="post">
					<button type="submit" name="checkout" class="ad13-btn ad13-checkout">Check out</button>
				</form>
			</div>
		</div>
		<p class="visually-hidden" aria-live="polite" data-ad13-status></p>
	</div>
</div>`,
		);

		const container = document.getElementById('ad13-cart');
		const panel = container.querySelector('.ad13-panel');
		const itemsList = container.querySelector('.ad13-items');
		const loading = container.querySelector('.ad13-loading');
		const emptyState = container.querySelector('.ad13-empty');
		const errorBox = container.querySelector('.ad13-error');
		const addedNote = container.querySelector('.ad13-added');
		const footer = container.querySelector('.ad13-foot');
		const subtotal = container.querySelector('[data-ad13-subtotal]');
		const status = container.querySelector('[data-ad13-status]');

		let isOpen = false;
		let opener = null;
		let busy = false;
		let cart = null;
		const compareAtCache = new Map(); // variant id -> compare-at price (cents), from /products/<handle>.js

		// ---------------------------------------------------------------------
		// Data
		// ---------------------------------------------------------------------
		async function loadCompareAtPrices(items) {
			const handles = [...new Set(items.filter((item) => !compareAtCache.has(item.variant_id)).map((item) => item.handle))];
			await Promise.all(
				handles.map((handle) =>
					fetch(`${root}products/${handle}.js`)
						.then((response) => (response.ok ? response.json() : null))
						.then((productData) => {
							if (!productData) return;
							productData.variants.forEach((variant) => compareAtCache.set(variant.id, variant.compare_at_price || 0));
						})
						.catch(() => {}),
				),
			);
		}

		async function fetchCart() {
			const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
			if (!response.ok) throw new Error('Could not load the cart');
			return response.json();
		}

		// Header count: re-render Dawn's cart-icon-bubble section (the mobile icon copies it).
		function updateCartIcon(sectionHtml) {
			const bubble = document.getElementById('cart-icon-bubble');
			if (!bubble || !sectionHtml) return;
			const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
			if (parsed) bubble.innerHTML = parsed.innerHTML;
		}

		function announce(message) {
			status.textContent = '';
			window.setTimeout(() => {
				status.textContent = message;
			}, 100);
		}

		function showError(message) {
			errorBox.textContent = message;
			errorBox.hidden = !message;
		}

		// ---------------------------------------------------------------------
		// Render
		// ---------------------------------------------------------------------
		function priceHtml(item, currency) {
			const compareAt = compareAtCache.get(item.variant_id) || 0;
			const regularLine = Math.max(compareAt > item.final_price ? compareAt * item.quantity : 0, item.original_line_price);
			const onSale = regularLine > item.final_line_price;
			return onSale
				? `<s><span class="visually-hidden">Regular price</span>${money(regularLine, currency)}</s><span class="visually-hidden">Sale price</span>${money(item.final_line_price, currency)}`
				: money(item.final_line_price, currency);
		}

		function render() {
			loading.hidden = true;
			const items = cart ? cart.items : [];
			const currency = cart && cart.currency;

			emptyState.hidden = items.length > 0;
			footer.hidden = items.length === 0;
			itemsList.hidden = items.length === 0;
			if (items.length === 0) addedNote.hidden = true;

			itemsList.innerHTML = items
				.map((item) => {
					const title = escapeHtml(item.product_title);
					const min = (item.quantity_rule && item.quantity_rule.min) || 1;
					const max = item.quantity_rule && item.quantity_rule.max;
					return `<li class="ad13-item" data-key="${escapeHtml(item.key)}">
	<a class="ad13-item__img" href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">
		${item.image ? `<img src="${escapeHtml(sizedImage(item.image, 160))}" alt="" width="62" height="62" loading="lazy">` : ''}
	</a>
	<div>
		<a class="ad13-item__title" href="${escapeHtml(item.url)}">${title}</a>
		<p class="ad13-item__price">${priceHtml(item, currency)}</p>
	</div>
	<div class="ad13-item__actions">
		<div class="ad13-qty" role="group" aria-label="Quantity for ${title}">
			<button type="button" data-ad13-qty="-1" aria-label="Decrease quantity of ${title}"${item.quantity <= min ? ' disabled' : ''}>&minus;</button>
			<span class="ad13-qty__value"><span class="visually-hidden">Quantity </span>${item.quantity}</span>
			<button type="button" data-ad13-qty="1" aria-label="Increase quantity of ${title}"${max && item.quantity >= max ? ' disabled' : ''}>+</button>
		</div>
		<button type="button" class="ad13-remove" data-ad13-remove aria-label="Remove ${title} from cart">${ICONS.trash}</button>
	</div>
</li>`;
				})
				.join('');

			subtotal.textContent = money(cart ? cart.total_price : 0, currency);
		}

		async function refresh() {
			try {
				cart = await fetchCart();
				await loadCompareAtPrices(cart.items);
				render();
			} catch (error) {
				loading.hidden = true;
				showError('We couldn’t load your cart. Please try again, or go to the cart page.');
				realWarn('[AD-13] cart load failed', error);
			}
		}

		// ---------------------------------------------------------------------
		// Quantity + remove
		// ---------------------------------------------------------------------
		async function changeLine(key, quantity, focusAfter) {
			if (busy) return;
			busy = true;
			container.classList.add('is-busy');
			container.setAttribute('aria-busy', 'true');
			showError('');

			const before = cart && cart.items.find((item) => item.key === key);
			try {
				const response = await fetch(`${root}cart/change.js`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ id: key, quantity, sections: 'cart-icon-bubble' }),
				});
				const data = await response.json();
				if (!response.ok || data.status) {
					showError(data.description || data.message || 'That quantity isn’t available.');
					await refresh();
				} else {
					cart = data;
					await loadCompareAtPrices(cart.items);
					render();
					updateCartIcon(data.sections && data.sections['cart-icon-bubble']);
					if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
						window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'ad13-cart', cartData: data });
					}
					const name = before ? before.product_title : 'Item';
					announce(quantity === 0 ? `${name} removed from your cart.` : `${name} quantity updated to ${quantity}.`);
				}
			} catch (error) {
				showError('Something went wrong updating your cart. Please try again.');
				realWarn('[AD-13] cart change failed', error);
			} finally {
				busy = false;
				container.classList.remove('is-busy');
				container.removeAttribute('aria-busy');
				restoreFocus(key, focusAfter);
			}
		}

		// After a re-render, put focus back on the same control (or a sensible neighbour).
		function restoreFocus(key, which) {
			if (!isOpen) return;
			const row = itemsList.querySelector(`[data-key="${CSS.escape(key)}"]`);
			let target = null;
			if (row && which) target = row.querySelector(which);
			if (target && target.disabled) target = row.querySelector('[data-ad13-remove]');
			if (!target) target = itemsList.querySelector('[data-ad13-remove]') || emptyState.querySelector('a') || panel;
			target.focus();
		}

		itemsList.addEventListener('click', (event) => {
			const row = event.target.closest('.ad13-item');
			if (!row || !cart) return;
			const item = cart.items.find((line) => line.key === row.dataset.key);
			if (!item) return;

			const stepButton = event.target.closest('[data-ad13-qty]');
			if (stepButton && !stepButton.disabled) {
				const step = Number(stepButton.dataset.ad13Qty);
				const selector = step > 0 ? '[data-ad13-qty="1"]' : '[data-ad13-qty="-1"]';
				changeLine(item.key, Math.max(0, item.quantity + step), selector);
			}
			if (event.target.closest('[data-ad13-remove]')) {
				changeLine(item.key, 0, '[data-ad13-remove]');
			}
		});

		// ---------------------------------------------------------------------
		// Open / close
		// ---------------------------------------------------------------------
		function headerBottom() {
			const header = document.querySelector('.section-header') || document.querySelector('header');
			if (!header) return 0;
			return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
		}

		function setMobileTop() {
			container.style.setProperty('--ad13-top', `${window.innerWidth < 768 ? headerBottom() : 0}px`);
		}

		async function openCart({ source, justAdded = false, cartData = null }) {
			ad13Track('cart_opened', { cart_source: source });
			if (!isOpen) opener = opener || document.activeElement;
			addedNote.hidden = !justAdded;
			showError('');

			if (!isOpen) {
				isOpen = true;
				setMobileTop();
				// Chat/accessibility/quiz widgets are injected after load at the same max z-index;
				// being last in <body> puts the cart on top of them.
				if (container !== document.body.lastElementChild) document.body.appendChild(container);
				container.hidden = false;
				document.documentElement.style.overflow = 'hidden';
				// next frame so the slide-in transition runs
				requestAnimationFrame(() => container.classList.add('is-open'));
				panel.focus();
			}

			if (cartData) {
				cart = cartData;
				await loadCompareAtPrices(cart.items);
				render();
			} else {
				if (!cart) loading.hidden = false;
				await refresh();
			}
			if (justAdded) announce('Item added to your cart.');
		}

		function closeCart() {
			if (!isOpen) return;
			isOpen = false;
			container.classList.remove('is-open');
			document.documentElement.style.overflow = '';
			const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			window.setTimeout(
				() => {
					if (!isOpen) container.hidden = true;
				},
				reduced ? 0 : 300,
			);
			if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus();
			opener = null;
		}

		container.addEventListener('click', (event) => {
			if (event.target.closest('[data-ad13-close]')) closeCart();
			if (event.target.closest('[data-ad13-continue]')) {
				ad13Track('continue_shopping_clicked', { cart_location: 'cart_screen' });
				closeCart();
			}
		});

		container.querySelector('form').addEventListener('submit', () => {
			ad13Track('checkout_clicked', { cart_location: 'cart_screen' });
		});

		// Esc closes; Tab stays inside the cart while it's open.
		document.addEventListener('keydown', (event) => {
			if (!isOpen) return;
			if (event.key === 'Escape') {
				event.preventDefault();
				closeCart();
				return;
			}
			if (event.key !== 'Tab') return;
			const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')].filter(
				(el) => el.offsetParent !== null && el.getAttribute('tabindex') !== '-1',
			);
			if (!focusable.length) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		});

		window.addEventListener('resize', () => {
			if (isOpen) setMobileTop();
		});

		// ---------------------------------------------------------------------
		// Hooks: header cart icons + every Dawn product form (via <cart-notification>)
		// ---------------------------------------------------------------------
		document.addEventListener(
			'click',
			(event) => {
				const icon = event.target.closest('#cart-icon-bubble, [data-id="mobile-cart-icon"]');
				if (!icon) return;
				// Let ctrl/cmd/shift-clicks open /cart in a new tab as usual.
				if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
				event.preventDefault();
				event.stopPropagation();
				if (isOpen) {
					closeCart();
				} else {
					opener = icon;
					openCart({ source: 'cart_icon' });
				}
			},
			true,
		);

		customElements.whenDefined('cart-notification').then(() => {
			const notification = document.querySelector('cart-notification');
			if (!notification) return;

			const originalSetActive = notification.setActiveElement && notification.setActiveElement.bind(notification);
			notification.setActiveElement = (element) => {
				opener = element;
				if (originalSetActive) originalSetActive(element);
			};

			// Product forms call this with the add-to-cart response instead of opening the notification.
			notification.renderContents = (parsedState) => {
				if (parsedState && parsedState.sections) updateCartIcon(parsedState.sections['cart-icon-bubble']);
				openCart({ source: 'add_to_cart', justAdded: true });
			};
		});
	})();
}

// --->  MANUAL A/B TEST RANDOMIZER <---
(function () {
	const preview = false;

	// =========================================================================
	// ---> TEST TARGETING CONFIGURATION
	// =========================================================================
	const config = {
		// URL Filtering (site-wide: the cart icon is on every page)
		filterByUrls: false,
		allowedUrls: [], // Only fires if current URL contains one of these strings

		// Device Filtering
		allowMobile: true,
		allowDesktop: true,
	};

	// Helper: Check if current URL is allowed
	function isUrlAllowed() {
		if (!config.filterByUrls) return true;
		// Using .includes() ensures query parameters won't break the match
		return config.allowedUrls.some((url) => window.location.href.includes(url));
	}

	function isDeviceAllowed() {
		const mobileBreakpoint = 768;
		const isMobile = window.innerWidth < mobileBreakpoint;

		if (isMobile && !config.allowMobile) return false;
		if (!isMobile && !config.allowDesktop) return false;

		return true;
	}

	// Evaluate targeting before running test or preview logic
	if (!isUrlAllowed() || !isDeviceAllowed()) {
		realLog('Test skipped: Does not meet URL or Device targeting rules.');
		return; // Exit out of the A/B test wrapper entirely
	}
	// =========================================================================

	if (!preview) {
		// CHANGE PER TEST: letters, numbers and underscores only (GA4 rejects hyphens).
		// Drives both the cookie and the GA4 user property, e.g. "ab_AD13".
		// Register "ab_" + testId in GA4 as a user-scoped custom dimension before launch.
		const testId = AD13.testId;
		const cookieName = 'ab_' + testId;

		// Helper to read cookies
		function getCookie(name) {
			const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
			return match ? match[2] : null;
		}

		// Check if user already has a variant cookie
		let variant = getCookie(cookieName);

		// If they are new, flip a coin (50/50 split) and assign them
		if (variant !== 'control' && variant !== 'v1') {
			variant = Math.random() < 0.5 ? 'control' : 'v1';

			// Write cookie so variant follows them everywhere (expires in 30 days)
			const date = new Date();
			date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
			document.cookie = cookieName + '=' + variant + ';expires=' + date.toUTCString() + ';path=/';
		}

		AD13.variant = variant;
		AD13.reporting = true;

		// =========================================================================
		// ---> SEND DIRECTLY TO GA4
		// =========================================================================
		window.dataLayer = window.dataLayer || [];
		function gtag() {
			dataLayer.push(arguments);
		}

		gtag('set', 'user_properties', {
			[cookieName]: variant,
		});

		// No debug_mode: GA4 treats the parameter as "debug" whenever it's present, even when false.
		gtag('event', 'experiment_impression', {
			experiment_id: testId,
			experiment_variant: variant,
			send_to: 'G-Y4VGNLBJPF',
		});
		// =========================================================================

		// Run the visual changes
		if (variant === 'v1') {
			realLog('MANUAL TEST: Variation active');
			testCode();
		} else {
			realLog('MANUAL TEST: Control active');
			ad13TrackControl();
		}

		// Send the page view goal (if you still have a separate tracker looking for gbEvents)
		window.gbEvents.push('Page View');

		// Only if a quick checkout tracker exists on the page (it isn't defined in this theme).
		if (typeof window.initQuickCheckoutTracking === 'function') {
			if (document.readyState === 'complete') {
				window.initQuickCheckoutTracking();
			} else {
				window.addEventListener('load', window.initQuickCheckoutTracking);
			}
		}
	} else {
		// Preview: "?ad-13" turns on the new cart screen for the rest of this browser tab, so it can be
		// tested across pages (add to cart on a product, open the cart from another page).
		// "?ad-13-off" turns it off again.
		let previewOn = false;
		try {
			if (window.location.href.includes('ad-13-off')) sessionStorage.removeItem('ad13_preview');
			else if (window.location.href.includes('ad-13')) sessionStorage.setItem('ad13_preview', '1');
			previewOn = sessionStorage.getItem('ad13_preview') === '1';
		} catch (error) {
			previewOn = window.location.href.includes('ad-13') && !window.location.href.includes('ad-13-off');
		}
		if (previewOn) {
			realLog('%cPreview mode ACTIVE', "color: #F7F631; background-color:black; font-weight: bold; font-family:'SpaceGrotesk', sans-serif;");
			testCode();
		}
	}
})();

})();
